import { useState } from 'react';

import {
  GetEcommerceOrderResponse as Order,
  GetPayLinkResponse as PayLink,
} from '@iwoca/lapi-client/edge';
import { fetchPostSharedUserAction } from '@iwoca/lapi-client/iwocapay';
import { useParams } from 'react-router';

import {
  useGetEcommerceOrder,
  useGetPayLinkByPayLinkId,
  useGetSellerByHandle,
} from '../../../api/lending/lapiHooks';
import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen';
import { useAccountNavigation } from '../../../hooks/useAccountNavigation';
import { useSellerWhitelabelling } from '../../../hooks/useSellerWhiteLabelling.hook';
import { OrderLinkIsNotValid } from '../../../Pages/FailurePages/OrderLinkIsNotValid';
import { OrderNotFound } from '../../../Pages/FailurePages/OrderNotFound';
import { PayLinkNotFound } from '../../../Pages/FailurePages/PayLinkNotFound';
import { FormalOffer } from '../../../Seller/PayLinks/utils/Paylinks.types';
import { useThreatMetrix } from '../../../utils/ThreatMetrix';
import { useThreatMetrixProfiling } from '../../../utils/threatMetrix/ThreatMetrixProfiling';
import { trackSelectedCheckoutOption } from '../../../utils/tracking';
import { CancelButton } from '../../components/CancelButton/CancelButton';
import { LoginBanner } from '../../components/LoginBanner';
import { PrivacyPolicy } from '../../components/PrivacyPolicy/privacyPolicy';
import { WhiteLabelOverride } from '../../components/WhiteLabelOverride/WhiteLabelOverride';
import {
  TEnabledOptions,
  useEnabledOptions,
} from '../../hooks/useEnabledTerms';
import { useIsOverlay } from '../../hooks/useIsOverlay';
import { useTrackLandingPageView } from '../../hooks/useTrackLandingPageView';
import { LoginPanel } from '../../LoginPanel/LoginPanel';
import { PayLaterDecisionModal } from '../../PayLinkLanding/components/PayLaterDecisionModal/PayLaterDecisionModal';
import { HelpTextAccordion } from '../../PayLinkLanding/HelpTextAccordion/HelpTextAccordion';
import { useGetValidIwocapayFormalOffers } from '../../PayLinkLanding/hooks/useGetValidIwocapayFormalOffers';
import { usePayLaterDecisionFlow } from '../../PayLinkLanding/hooks/usePayLaterDecisionFlow';
import {
  submitBuyerStepZeroNewCustomer,
  submitBuyerStepZeroReturningCustomer,
} from '../../PayLinkLanding/PayLater/CreateUserForm/submitBuyerStepZero';
import { PaymentDetails } from '../../PayLinkLanding/PaymentDetails/PaymentDetails';
import { FormValues } from '../../PayLinkLanding/utils/PayLinkLanding.types';
import {
  getBuyerInterest,
  getPricingPromotions,
} from '../../PayLinkLanding/utils/productPricingHelpers';
import { redirectAfterPayLater } from '../../PayLinkLanding/utils/redirectAfterPayLater';
import { payNowCheckoutOnPayLink } from '../../PayNow/checkout';
import { SIGNUP_BASE_PATH } from '../../Signup/routes';
import {
  durationFromPricingOption,
  productFromDuration,
} from '../../utils/product';
import { buildQueryString } from '../../utils/queryParams';

export const OrderCheckoutPage = () => {
  const { navigateToAccount } = useAccountNavigation();
  const decisionFlow = usePayLaterDecisionFlow();
  const { orderId } = useParams();

  const { ecommerceOrder, loadingEcommerceOrder, ecommerceOrderError } =
    useGetEcommerceOrder({
      orderId,
    });

  const { payLink, loadingPayLink } = useGetPayLinkByPayLinkId({
    payLinkId: ecommerceOrder?.pay_link_id,
  });

  const { formalOffers, loadingFormalOffers } =
    useGetValidIwocapayFormalOffers();

  const { seller, loadingSeller } = useGetSellerByHandle(
    payLink?.seller_handle,
  );

  // White-label theme
  useSellerWhitelabelling(payLink?.seller_handle);

  useThreatMetrixProfiling(payLink?.seller_handle, payLink?.id);

  const { enabledOptions, loadingOptions } = useEnabledOptions({
    pricing: payLink?.product_pricing,
    allowedOptions: ecommerceOrder?.allowed_payment_terms,
  });

  const [loginPanelOpen, setLoginPanelOpen] = useState(false);

  useTrackLandingPageView({
    payLinkType: 'custom',
    visibleOptions: enabledOptions,
    payLinkId: payLink?.id,
    productPricing: payLink?.product_pricing!,
    sellerId: seller?.seller_id,
    loadingApis: loadingSeller || loadingPayLink,
  });

  useThreatMetrix();

  const isOverlay = useIsOverlay();
  const cancelUrl = payLink?.cancel_url;

  if (ecommerceOrderError) {
    return <OrderNotFound />;
  }

  if (
    (orderId && loadingEcommerceOrder) ||
    loadingPayLink ||
    loadingOptions ||
    loadingFormalOffers ||
    loadingSeller
  ) {
    return <LoadingScreen />;
  }

  if (!ecommerceOrder) {
    return <OrderNotFound />;
  }

  if (!payLink) {
    // This shouldn't happen, but helps typescript to infer the following types correctly
    return <PayLinkNotFound />;
  }

  if (ecommerceOrder.status !== 'CREATED') {
    return <OrderLinkIsNotValid />;
  }

  const pricingPromotions = getPricingPromotions({
    productPricing: payLink.product_pricing,
  });

  const buyerInterest = getBuyerInterest({
    productPricing: payLink.product_pricing,
    formalOffers: formalOffers,
  });

  return (
    <div className="min-h-screen w-full text-left text-m leading-[150%] text-denim-25">
      <div className="mx-auto flex max-w-[1440px] flex-col">
        <div className="m700:grid m700:grow m700:grid-cols-[1fr_2fr] m700:gap-[2rem] m900:grid-cols-[1fr_2fr_1fr] m900:[grid-template-areas:'._payment-details_help-accordion']">
          <div className="m700:mt-2xl m900:[grid-area:payment-details]">
            <CancelButton href={cancelUrl ?? undefined} />
            <WhiteLabelOverride componentType="background">
              <LoginBanner onClick={() => setLoginPanelOpen(true)} />
            </WhiteLabelOverride>
            <PaymentDetails
              isPreview={false}
              sellerName={payLink.seller_trading_name}
              pricingPromotions={pricingPromotions}
              buyerInterest={buyerInterest}
              onSubmit={onSubmit(
                payLink,
                { data: ecommerceOrder },
                enabledOptions,
                isOverlay,
                decisionFlow,
                formalOffers,
                navigateToAccount,
              )}
              initialValues={{
                amount: payLink.amount.toString(),
                reference: payLink.reference,
              }}
              lockAmount={true}
              lockReference={true}
              enabledOptions={enabledOptions}
              payLinkType="order"
            />
            <PrivacyPolicy />
          </div>
          <div className="m700:flex m700:flex-col m700:justify-between m900:[grid-area:help-accordion]">
            <HelpTextAccordion
              className="m-[1rem] mt-0 m700:mt-2xl"
              enabledOptions={enabledOptions}
            />
          </div>
        </div>
      </div>
      <LoginPanel
        open={loginPanelOpen}
        close={() => setLoginPanelOpen(false)}
      />
      <PayLaterDecisionModal status={decisionFlow.status} />
    </div>
  );
};

const onSubmit =
  (
    payLink: PayLink,
    order: Order,
    enabledOptions: TEnabledOptions,
    isOverlay: boolean,
    decisionFlow: ReturnType<typeof usePayLaterDecisionFlow>,
    formalOffers?: FormalOffer[],
    navigateToAccount?: (queryString?: string) => void,
  ) =>
  ({ values, stateKey }: { values: FormValues; stateKey?: string }) => {
    const {
      paymentOption,
      payNowFullName,
      payNowEmailAddress,
      payNowMarketingConsent: marketingConsent,
    } = values;

    if (paymentOption === 'payNow')
      return payNowCheckoutOnPayLink({
        payLinkId: payLink.id,
        customerDetails: {
          fullName: payNowFullName,
          emailAddress: payNowEmailAddress,
        },
        marketingConsent,
        isOverlay,
      });

    const baseParams = {
      values,
      payLink,
      order,
      formalOffers,
      enabledOptions,
      decisionFlow,
    };

    if (stateKey && navigateToAccount) {
      return onSubmitPayLater({
        ...baseParams,
        stateKey,
        navigateToAccount,
      });
    }

    return onSubmitPayLater(baseParams);
  };

const onSubmitPayLater = async ({
  values,
  payLink,
  order,
  stateKey,
  formalOffers,
  enabledOptions,
  navigateToAccount,
  decisionFlow,
}: {
  values: FormValues;
  payLink: PayLink;
  order: Order;
  formalOffers?: FormalOffer[];
  enabledOptions: TEnabledOptions;
  decisionFlow: ReturnType<typeof usePayLaterDecisionFlow>;
} & (
  | {
      stateKey: string;
      navigateToAccount: (queryString?: string) => void;
    }
  | {
      stateKey?: undefined;
      navigateToAccount?: undefined;
    }
)) => {
  const queryString = buildQueryString({
    orderId: order.data.id,
    payLinkId: order.data.pay_link_id,
  });

  const selectedOption = values.paymentOption
    ? productFromDuration[durationFromPricingOption[values.paymentOption]!]
    : 'THREE_MONTHS';

  trackSelectedCheckoutOption({
    payLinkId: payLink.id,
    visibleOptions: enabledOptions,
    selectedOption: selectedOption,
  });

  if (stateKey) {
    await submitBuyerStepZeroReturningCustomer(
      stateKey,
      payLink,
      values.paymentOption!,
    );

    await fetchPostSharedUserAction({
      stateKey,
      body: { user_action: 'CHECKOUT_PAY_LINK' },
    });

    const payLinkAmount = parseFloat(values.amount);
    const queryParams = {
      orderId: order.data.id,
      payLinkId: order.data.pay_link_id,
    };
    redirectAfterPayLater({
      paymentOption: values.paymentOption,
      payLinkAmount,
      formalOffers,
      navigateToAccount,
      onAwaitDecision: decisionFlow.isEnabled
        ? () => void decisionFlow.trigger({ payLinkAmount, queryParams })
        : undefined,
      queryParams,
    });
  } else {
    const customerState = await submitBuyerStepZeroNewCustomer(
      values.emailAddress,
      values.marketingOptIn,
      payLink,
      values.paymentOption!,
    );

    await fetchPostSharedUserAction({
      stateKey: customerState.data!.state_key,
      body: { user_action: 'CHECKOUT_PAY_LINK' },
    });

    window.location.href = `${SIGNUP_BASE_PATH}${queryString}`;
  }
};
